import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import FancySelect from "../components/FancySelect"; // ajusta ruta

const ScenathonLobby = () => {
  const navigate = useNavigate();
  const {
    user,
    bootstrapping,
    authFetch,
    login,
    logout,
    selectedScenathon: activeScenathon,
    setSelectedScenathon,
  } = useAuth();

  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [error, setError] = useState("");
  const [entering, setEntering] = useState(false);

  // dropdowns
  const [scenathons, setScenathons] = useState([]);

  const [selectedScenathonId, setSelectedScenathonId] = useState("");

  useEffect(() => {
    if (activeScenathon?.id) {
      setSelectedScenathonId(String(activeScenathon.id));
    }
  }, [activeScenathon]);

  // cargar user + opciones iniciales
  useEffect(() => {
    if (bootstrapping) return;

    if (!user) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError("");

        // 1) Sesión / user
        const meRes = await authFetch("/api/auth/me");
        if (!meRes.ok) throw new Error("Not authorized");
        const meData = await meRes.json().catch(() => ({}));
        setSessionInfo(meData.user || null);

        // 2) Scenathons
        const scRes = await authFetch("/api/scenathon/scenathons");
        if (!scRes.ok) throw new Error("Could not load scenathons");
        const scData = await scRes.json().catch(() => ({}));

        const list = Array.isArray(scData) ? scData : scData.scenathons || [];
        setScenathons(list);

        // opcional: autoselección si solo hay uno
        if (list.length === 1) {
          setSelectedScenathonId(String(list[0].id));
        }
      } catch (e) {
        console.error(e);
        setError("Could not load the lobby.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapping, user, navigate]);

  console.log("sesion info: ",sessionInfo);

  const scenathonOptions = useMemo(() => {
    return scenathons.map((s) => ({
      value: String(s.id),
      label: s.name || s.title || `Scenathon ${s.id}`,
    }));
  }, [scenathons]);

  const canEnter = Boolean(selectedScenathonId && !loading && !entering);

  const handleEnterGame = async () => {
    if (!canEnter) return;

    setEntering(true);
    setError("");

    try {
      const response = await authFetch("/api/auth/gameslog", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ scenathon_id: Number(selectedScenathonId) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Unable to create scenathon session");
      }

      const data = await response.json();
      if (data.token) {
        login({ token: data.token, user: data.user || user });
      }

      const selectedEntry = scenathons.find((item) => String(item.id) === selectedScenathonId);
      if (selectedEntry) {
        setSelectedScenathon({ id: Number(selectedScenathonId), name: selectedEntry.name || selectedEntry.title || `Scenathon ${selectedScenathonId}` });
      }

      const roleId = Number(user?.type_id || 0);
      let targetPath = "/scenathon";

      if (roleId === 1) {
        targetPath = "/admin/scenathon";
      } else if ([3,4].includes(roleId)) {
        targetPath = "/pathways";
      } else if ([2, 5].includes(roleId)) {
        targetPath = "/scenathon";
      }

      navigate(targetPath, {
        state: {
          scenathonId: selectedScenathonId,
        },
        replace: false,
      });
    } catch (err) {
      console.error(err);
      setError(String(err.message || "Unable to enter scenathon."));
    } finally {
      setEntering(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (bootstrapping || loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-lg">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="w-screen min-h-screen bg-gray-100 flex justify-center items-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Scenathon Lobby
        </h1>
        <p className="text-sm text-gray-600 mb-8">
          Select a scenathon to continue.
        </p>

        {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

        {/* info user */}
        {sessionInfo && (
          <div className="mb-8 space-y-2 text-gray-700">
            <div>
              <span className="font-semibold">Name:</span>{" "}
              {sessionInfo.first_name+" "+(sessionInfo.second_name || "")+" "+sessionInfo.last_name || "-"}
            </div>
            <div>
              <span className="font-semibold">Email:</span>{" "}
              {sessionInfo.email || "-"}
            </div>
            <div>
              <span className="font-semibold">Organization:</span>{" "}
              {sessionInfo.organization_name || "-"}
            </div>
            <div>
              <span className="font-semibold">Country:</span>{" "}
              {sessionInfo.country || "Admin"}
            </div>
          </div>
        )}

        {/* selects */}
        <div className="space-y-6">
          <FancySelect
            label="Scenathon"
            value={selectedScenathonId}
            onChange={(v) => setSelectedScenathonId(String(v))}
            options={scenathonOptions}
            placeholder="Select a scenathon..."
            disabled={scenathonOptions.length === 0}
          />

        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-10">
          <button
            onClick={handleEnterGame}
            disabled={!canEnter}
            className={`w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg ${
              !canEnter ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            Enter Scenathon
          </button>

          <button
            onClick={handleLogout}
            className="w-full border border-gray-300 text-gray-800 font-medium py-3 rounded-lg hover:bg-gray-50"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScenathonLobby;
